import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getQuotes, createQuote, updateQuote, deleteQuote, sendQuote,
  acceptQuote, rejectQuote, duplicateQuote, convertQuoteToSale,
  downloadQuotePDF, getBranches, getProducts, getClients,
  getPOSTerminals, getCashiers
} from '../../services/api';
import {
  Plus, Search, Edit, Trash2, X, ClipboardList, Send, Check, XCircle,
  Copy, ShoppingCart, FileDown, Loader2, ArrowLeft, ChevronDown, Eye, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-yellow-100 text-yellow-700',
  converted: 'bg-purple-100 text-purple-700',
};

const STATUS_LABELS = {
  draft: 'Borrador',
  sent: 'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  expired: 'Vencida',
  converted: 'Convertida',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateStr.slice(0, 10); }
};

const Cotizaciones = () => {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // list | form
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Branch data
  const [branches, setBranches] = useState([]);

  // Modals
  const [showSendModal, setShowSendModal] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(null);
  const [convertData, setConvertData] = useState({ terminal_id: '', cashier_id: '', payment_method: 'cash' });
  const [terminals, setTerminals] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, right: 0 });

  // Form state
  const [formData, setFormData] = useState({
    branch_id: '',
    validity_days: 15,
    client: { name: '', email: '', phone: '', company: '', client_id: null },
    items: [],
    subtotal: 0,
    total_discount: 0,
    total_tax: 0,
    total: 0,
    notes: '',
    terms: '',
  });

  // Product/client search for form
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientDiscount, setSelectedClientDiscount] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const clientSearchRef = useRef(null);
  const productSearchRef = useRef(null);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    loadQuotes();
    loadBranches();
  }, [user]);

  const loadQuotes = async () => {
    if (!user?.business_id) return;
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.q = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (dateStart) params.start_date = new Date(dateStart).toISOString();
      if (dateEnd) params.end_date = new Date(dateEnd + 'T23:59:59').toISOString();
      const res = await getQuotes(params);
      setQuotes(res.data);
    } catch (err) {
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    if (!user?.business_id) return;
    try {
      const res = await getBranches(user.business_id);
      setBranches(res.data);
    } catch {}
  };

  useEffect(() => {
    const timer = setTimeout(() => { loadQuotes(); }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, dateStart, dateEnd]);

  // Client search debounced
  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await getClients(user.business_id, { q: clientSearch });
        setClientResults(res.data);
        setShowClientDropdown(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Product search debounced
  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await getProducts({ business_id: user.business_id, search: productSearch });
        setProductResults(res.data);
        setShowProductDropdown(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target)) setShowClientDropdown(false);
      if (productSearchRef.current && !productSearchRef.current.contains(e.target)) setShowProductDropdown(false);
      if (actionMenuRef.current && actionMenuRef.current.contains(e.target)) return;
      setActionMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Recalculate totals when items change
  const recalcTotals = useCallback((items) => {
    let subtotal = 0, totalDiscount = 0, totalTax = 0;
    items.forEach(item => {
      subtotal += item.line_subtotal;
      totalDiscount += (item.original_unit_price - item.unit_price) * item.quantity;
      totalTax += item.line_tax;
    });
    const total = subtotal + totalTax;
    setFormData(prev => ({ ...prev, items, subtotal, total_discount: Math.max(0, totalDiscount), total_tax: totalTax, total }));
  }, []);

  const selectClient = (client) => {
    const discount = client.discount_default_percent || 0;
    setSelectedClientDiscount(discount);
    setFormData(prev => ({
      ...prev,
      client: {
        name: client.razon_social,
        email: client.email || '',
        phone: client.phone || '',
        company: client.razon_social,
        client_id: client.id,
      }
    }));
    setClientSearch(client.razon_social);
    setShowClientDropdown(false);

    // Re-apply discount to existing items with base price
    if (discount > 0 && formData.items.length > 0) {
      const updated = formData.items.map(item => {
        if (item.price_source === 'base' || item.price_source === 'client_discount') {
          const newPrice = item.original_unit_price * (1 - discount / 100);
          const lineSub = newPrice * item.quantity;
          const tax = item.tax_config.iva_exempt ? 0 : lineSub * item.tax_config.iva_rate;
          return { ...item, unit_price: newPrice, discount_percent: discount, price_source: 'client_discount', line_subtotal: lineSub, line_tax: tax, line_total: lineSub + tax };
        }
        return item;
      });
      recalcTotals(updated);
    }
  };

  const addProductItem = (product) => {
    const price = product.pricing?.retail_price || 0;
    const taxConfig = {
      iva_rate: product.tax_config?.iva_rate ?? 0.16,
      iva_exempt: product.tax_config?.iva_exempt ?? false,
      tax_object: product.tax_config?.tax_object ?? '02',
      ieps_rate: product.tax_config?.ieps_rate ?? 0,
      ieps_exempt: product.tax_config?.ieps_exempt ?? true,
    };
    const discount = selectedClientDiscount;
    const finalPrice = discount > 0 ? price * (1 - discount / 100) : price;
    const lineSub = finalPrice * 1;
    const tax = taxConfig.iva_exempt ? 0 : lineSub * taxConfig.iva_rate;

    const newItem = {
      item_id: crypto.randomUUID(),
      product_id: product.id,
      product_sku: product.sku,
      concept: product.name,
      description: product.description_short || '',
      quantity: 1,
      original_unit_price: price,
      unit_price: finalPrice,
      discount_percent: discount,
      price_source: discount > 0 ? 'client_discount' : 'base',
      tax_config: taxConfig,
      line_subtotal: lineSub,
      line_tax: tax,
      line_total: lineSub + tax,
    };
    const updated = [...formData.items, newItem];
    recalcTotals(updated);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const addFreeItem = () => {
    const newItem = {
      item_id: crypto.randomUUID(),
      product_id: null,
      product_sku: null,
      concept: '',
      description: '',
      quantity: 1,
      original_unit_price: 0,
      unit_price: 0,
      discount_percent: 0,
      price_source: 'manual_override',
      tax_config: { iva_rate: 0.16, iva_exempt: false, tax_object: '02', ieps_rate: 0, ieps_exempt: true },
      line_subtotal: 0,
      line_tax: 0,
      line_total: 0,
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...formData.items];
    const item = { ...items[idx] };

    if (field === 'concept' || field === 'description') {
      item[field] = value;
    } else if (field === 'quantity') {
      item.quantity = Math.max(0.01, parseFloat(value) || 0);
    } else if (field === 'unit_price') {
      item.unit_price = parseFloat(value) || 0;
      item.price_source = 'manual_override';
      if (item.original_unit_price > 0) {
        item.discount_percent = Math.max(0, ((item.original_unit_price - item.unit_price) / item.original_unit_price) * 100);
      }
    } else if (field === 'discount_percent') {
      item.discount_percent = Math.min(100, Math.max(0, parseFloat(value) || 0));
      item.unit_price = item.original_unit_price * (1 - item.discount_percent / 100);
      item.price_source = 'manual_override';
    } else if (field === 'original_unit_price') {
      item.original_unit_price = parseFloat(value) || 0;
      item.unit_price = item.original_unit_price * (1 - (item.discount_percent || 0) / 100);
    }

    // Recalc line
    item.line_subtotal = item.unit_price * item.quantity;
    item.line_tax = item.tax_config.iva_exempt ? 0 : item.line_subtotal * item.tax_config.iva_rate;
    item.line_total = item.line_subtotal + item.line_tax;
    items[idx] = item;
    recalcTotals(items);
  };

  const removeItem = (idx) => {
    const items = formData.items.filter((_, i) => i !== idx);
    recalcTotals(items);
  };

  const resetForm = () => {
    setFormData({
      branch_id: branches.length === 1 ? branches[0].id : '',
      validity_days: 15,
      client: { name: '', email: '', phone: '', company: '', client_id: null },
      items: [],
      subtotal: 0, total_discount: 0, total_tax: 0, total: 0,
      notes: '', terms: '',
    });
    setClientSearch('');
    setSelectedClientDiscount(0);
    setEditingQuote(null);
  };

  const openForm = (quote = null) => {
    if (quote) {
      setEditingQuote(quote);
      setFormData({
        branch_id: quote.branch_id,
        validity_days: quote.validity_days || 15,
        client: quote.client || { name: '', email: '', phone: '', company: '', client_id: null },
        items: quote.items || [],
        subtotal: quote.subtotal, total_discount: quote.total_discount, total_tax: quote.total_tax, total: quote.total,
        notes: quote.notes || '', terms: quote.terms || '',
      });
      setClientSearch(quote.client?.name || '');
    } else {
      resetForm();
    }
    setView('form');
  };

  const handleSubmit = async (sendAfter = false) => {
    if (!formData.branch_id) { toast.error('Selecciona una sucursal'); return; }
    if (!formData.client.name) { toast.error('Ingresa el nombre del cliente'); return; }
    if (formData.items.length === 0) { toast.error('Agrega al menos un concepto'); return; }

    setSaving(true);
    try {
      let res;
      if (editingQuote) {
        res = await updateQuote(editingQuote.id, formData);
        toast.success('Cotizacion actualizada');
      } else {
        res = await createQuote(formData);
        toast.success('Cotizacion creada');
      }

      if (sendAfter && res.data?.id) {
        try {
          await sendQuote(res.data.id, { email: formData.client.email || null });
          toast.success('Cotizacion enviada');
        } catch { toast.error('Error al enviar cotizacion'); }
      }

      setView('list');
      resetForm();
      loadQuotes();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar cotizacion'));
    } finally {
      setSaving(false);
    }
  };

  // Actions
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta cotizacion?')) return;
    try {
      await deleteQuote(id);
      toast.success('Cotizacion eliminada');
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error al eliminar')); }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateQuote(id);
      toast.success('Cotizacion duplicada');
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error al duplicar')); }
  };

  const handleSendConfirm = async () => {
    if (!showSendModal) return;
    try {
      await sendQuote(showSendModal.id, { email: sendEmail || null });
      toast.success('Cotizacion enviada');
      setShowSendModal(null);
      setSendEmail('');
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error al enviar')); }
  };

  const handleAccept = async (id) => {
    try {
      await acceptQuote(id);
      toast.success('Cotizacion aceptada');
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error')); }
  };

  const handleReject = async (id) => {
    try {
      await rejectQuote(id);
      toast.success('Cotizacion rechazada');
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error')); }
  };

  const handleDownloadPDF = async (id, folio) => {
    try {
      const res = await downloadQuotePDF(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cotizacion_${folio}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar PDF'); }
  };

  const openConvertModal = async (quote) => {
    setShowConvertModal(quote);
    setConvertData({ terminal_id: '', cashier_id: '', payment_method: 'cash' });
    try {
      if (quote.branch_id) {
        const [tRes, cRes] = await Promise.all([
          getPOSTerminals(quote.branch_id),
          getCashiers(user.business_id)
        ]);
        setTerminals(tRes.data);
        setCashiers(cRes.data);
      }
    } catch {}
  };

  const handleConvertConfirm = async () => {
    if (!showConvertModal) return;
    if (!convertData.terminal_id || !convertData.cashier_id) {
      toast.error('Selecciona terminal y cajero');
      return;
    }
    try {
      const res = await convertQuoteToSale(showConvertModal.id, convertData);
      toast.success(`Venta creada: ${res.data.ticket_number}`);
      setShowConvertModal(null);
      loadQuotes();
    } catch (err) { toast.error(getErrorMessage(err, 'Error al convertir')); }
  };

  const copyPublicLink = (token) => {
    const link = `${window.location.origin}/q/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  // Stats
  const stats = {
    total: quotes.length,
    draft: quotes.filter(q => q.status === 'draft').length,
    sent: quotes.filter(q => q.status === 'sent').length,
    accepted: quotes.filter(q => q.status === 'accepted').length,
  };

  const priceBadge = (source) => {
    if (source === 'client_discount') return <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Desc. Cliente</span>;
    if (source === 'manual_override') return <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Manual</span>;
    return <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Base</span>;
  };

  // ==================== RENDER ====================

  if (loading && view === 'list') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
        </div>
      </DashboardLayout>
    );
  }

  // ==================== FORM VIEW ====================
  if (view === 'form') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <button onClick={() => { setView('list'); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-slate-900">{editingQuote ? 'Editar Cotizacion' : 'Nueva Cotizacion'}</h1>
          </div>

          {/* Branch + Validity */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sucursal *</label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, branch_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Seleccionar sucursal</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Vigencia</label>
                <select
                  value={formData.validity_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, validity_days: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value={7}>7 dias</option>
                  <option value={15}>15 dias</option>
                  <option value={30}>30 dias</option>
                </select>
              </div>
            </div>
          </div>

          {/* Client */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative" ref={clientSearchRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">Buscar cliente existente</label>
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Buscar por nombre, RFC..."
                />
                {showClientDropdown && clientResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {clientResults.map(c => (
                      <button key={c.id} onClick={() => selectClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                        <span className="font-medium">{c.razon_social}</span>
                        <span className="text-slate-500 ml-2">{c.rfc}</span>
                        {c.discount_default_percent > 0 && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1 rounded">{c.discount_default_percent}% desc.</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.client.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, name: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Nombre del cliente"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.client.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, email: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={formData.client.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, phone: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="(000) 000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa</label>
                <input
                  type="text"
                  value={formData.client.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, client: { ...prev.client, company: e.target.value } }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Nombre de empresa"
                />
              </div>
            </div>
            {selectedClientDiscount > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                Descuento cliente: {selectedClientDiscount}%
              </div>
            )}
          </div>

          {/* Items */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Conceptos</h3>
              <div className="flex gap-2">
                <div className="relative" ref={productSearchRef}>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 w-56"
                    placeholder="Buscar producto..."
                  />
                  {showProductDropdown && productResults.length > 0 && (
                    <div className="absolute z-20 w-72 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {productResults.map(p => (
                        <button key={p.id} onClick={() => addProductItem(p)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                          <span className="font-medium">{p.name}</span>
                          <span className="text-slate-500 ml-2">{formatCurrency(p.pricing?.retail_price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={addFreeItem} className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors">
                  <Plus className="w-4 h-4" /> Concepto libre
                </button>
              </div>
            </div>

            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-2" />
                <p>Agrega productos o conceptos libres</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Concepto</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 hidden md:table-cell">Descripcion</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 w-20">Cant.</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 w-28">P. Original</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 w-20">Desc.%</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 w-28">P. Final</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 w-28">Total</th>
                      <th className="px-3 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formData.items.map((item, idx) => (
                      <tr key={item.item_id}>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={item.concept}
                            onChange={(e) => updateItem(idx, 'concept', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                            placeholder="Concepto"
                          />
                          <div className="mt-1">{priceBadge(item.price_source)}</div>
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell">
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => updateItem(idx, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                            placeholder="Descripcion"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right"
                            min="0.01" step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.original_unit_price}
                            onChange={(e) => updateItem(idx, 'original_unit_price', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right"
                            min="0" step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.discount_percent}
                            onChange={(e) => updateItem(idx, 'discount_percent', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right"
                            min="0" max="100" step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right"
                            min="0" step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.line_total)}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeItem(idx)} className="p-1 text-slate-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Totals + Notes side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-3">Notas y Condiciones</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    rows={3} placeholder="Notas visibles para el cliente..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Condiciones</label>
                  <textarea
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    rows={3} placeholder="Terminos y condiciones..."
                  />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-3">Totales</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{formatCurrency(formData.subtotal)}</span></div>
                {formData.total_discount > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Descuento</span><span className="text-red-600">-{formatCurrency(formData.total_discount)}</span></div>
                )}
                <div className="flex justify-between text-sm"><span className="text-slate-500">Impuestos</span><span>{formatCurrency(formData.total_tax)}</span></div>
                <div className="border-t border-slate-200 pt-2 flex justify-between text-lg font-bold">
                  <span>TOTAL</span><span className="text-teal-700">{formatCurrency(formData.total)}</span>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => { setView('list'); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-sm">
                  Cancelar
                </button>
                <button onClick={() => handleSubmit(false)} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Guardar Borrador
                </button>
                <button onClick={() => handleSubmit(true)} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:opacity-50 transition-colors text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Guardar y Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ==================== LIST VIEW ====================
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Cotizaciones</h1>
            <p className="text-slate-600 mt-2">Crea y gestiona cotizaciones para tus clientes</p>
          </div>
          <button onClick={() => openForm()} className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors">
            <Plus className="w-5 h-5" /> Nueva Cotizacion
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-slate-600', bg: 'bg-slate-50' },
            { label: 'Borradores', value: stats.draft, color: 'text-slate-600', bg: 'bg-slate-50' },
            { label: 'Enviadas', value: stats.sent, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Aceptadas', value: stats.accepted, color: 'text-green-600', bg: 'bg-green-50' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${s.bg} rounded-lg`}>
                  <ClipboardList className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                  <p className="text-sm text-slate-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" placeholder="Buscar por folio, cliente..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {quotes.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay cotizaciones</p>
              <p className="text-slate-400 text-sm mt-1">Crea tu primera cotizacion para empezar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Folio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden lg:table-cell">Vigencia</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quotes.map((q) => (
                    <tr key={q.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3"><span className="font-mono text-sm font-medium">{q.folio}</span></td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900 text-sm">{q.client?.name}</span>
                        {q.client?.company && q.client.company !== q.client.name && (
                          <span className="block text-xs text-slate-500">{q.client.company}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">{formatDate(q.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">{formatDate(q.expiry_date)}</td>
                      <td className="px-4 py-3 text-right font-medium text-sm">{formatCurrency(q.total)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[q.status] || q.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="relative inline-block">
                          <button onClick={(e) => {
                            e.stopPropagation();
                            if (actionMenuId === q.id) { setActionMenuId(null); return; }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setActionMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            setActionMenuId(q.id);
                          }}
                            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-slate-500" />
                          </button>
                          {actionMenuId === q.id && (
                            <div
                              ref={actionMenuRef}
                              className="fixed w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-[9999] py-1"
                              style={{ top: actionMenuPos.top, right: actionMenuPos.right }}
                            >
                              {q.status === 'draft' && (
                                <button onClick={() => { setActionMenuId(null); openForm(q); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                  <Edit className="w-4 h-4" /> Editar
                                </button>
                              )}
                              <button onClick={() => { setActionMenuId(null); handleDownloadPDF(q.id, q.folio); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                <FileDown className="w-4 h-4" /> Descargar PDF
                              </button>
                              <button onClick={() => { setActionMenuId(null); copyPublicLink(q.public_token); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                <Copy className="w-4 h-4" /> Copiar link
                              </button>
                              {(q.status === 'draft' || q.status === 'sent') && (
                                <button onClick={() => { setActionMenuId(null); setShowSendModal(q); setSendEmail(q.client?.email || ''); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                  <Send className="w-4 h-4" /> Enviar
                                </button>
                              )}
                              {(q.status === 'draft' || q.status === 'sent') && (
                                <button onClick={() => { setActionMenuId(null); handleAccept(q.id); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-green-700">
                                  <Check className="w-4 h-4" /> Aceptar
                                </button>
                              )}
                              {q.status !== 'converted' && q.status !== 'rejected' && (
                                <button onClick={() => { setActionMenuId(null); handleReject(q.id); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600">
                                  <XCircle className="w-4 h-4" /> Rechazar
                                </button>
                              )}
                              <button onClick={() => { setActionMenuId(null); handleDuplicate(q.id); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                <Copy className="w-4 h-4" /> Duplicar
                              </button>
                              {q.status === 'accepted' && (
                                <button onClick={() => { setActionMenuId(null); openConvertModal(q); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-purple-700">
                                  <ShoppingCart className="w-4 h-4" /> Convertir a Venta
                                </button>
                              )}
                              {q.status === 'draft' && (
                                <button onClick={() => { setActionMenuId(null); handleDelete(q.id); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600">
                                  <Trash2 className="w-4 h-4" /> Eliminar
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900">Enviar Cotizacion</h3>
              <button onClick={() => setShowSendModal(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Folio: <strong>{showSendModal.folio}</strong></p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Email del cliente</label>
              <input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="email@ejemplo.com" />
            </div>
            <div className="mb-4">
              <button onClick={() => copyPublicLink(showSendModal.public_token)}
                className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800">
                <Copy className="w-4 h-4" /> Copiar link publico
              </button>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowSendModal(null)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm">Cancelar</button>
              <button onClick={handleSendConfirm} className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 text-sm flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900">Convertir a Venta</h3>
              <button onClick={() => setShowConvertModal(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Cotizacion <strong>{showConvertModal.folio}</strong> por {formatCurrency(showConvertModal.total)}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Terminal *</label>
                <select value={convertData.terminal_id} onChange={(e) => setConvertData(prev => ({ ...prev, terminal_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Seleccionar terminal</option>
                  {terminals.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cajero *</label>
                <select value={convertData.cashier_id} onChange={(e) => setConvertData(prev => ({ ...prev, cashier_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Seleccionar cajero</option>
                  {cashiers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Metodo de pago</label>
                <select value={convertData.payment_method} onChange={(e) => setConvertData(prev => ({ ...prev, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="cash">Efectivo</option>
                  <option value="debit">Debito</option>
                  <option value="credit">Credito</option>
                  <option value="transfer">Transferencia</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowConvertModal(null)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm">Cancelar</button>
              <button onClick={handleConvertConfirm} className="flex-1 px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 text-sm flex items-center justify-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Convertir
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Cotizaciones;
